import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import { motion, AnimatePresence } from 'framer-motion';
import { FaPlus, FaTrash, FaSave, FaFileInvoiceDollar, FaEdit, FaArrowLeft } from 'react-icons/fa';
import toast from 'react-hot-toast';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import logo from '../assets/logo.png';
import { safeFetch } from '../utils/apiUtils';
import './AdminInvoice.css';

/* ── GST state-code lookup (first 2 digits of GSTIN) ── */
const GST_STATE_CODES = {
    '01':'Jammu & Kashmir','02':'Himachal Pradesh','03':'Punjab',
    '04':'Chandigarh','05':'Uttarakhand','06':'Haryana','07':'Delhi',
    '08':'Rajasthan','09':'Uttar Pradesh','10':'Bihar','11':'Sikkim',
    '12':'Arunachal Pradesh','13':'Nagaland','14':'Manipur','15':'Mizoram',
    '16':'Tripura','17':'Meghalaya','18':'Assam','19':'West Bengal',
    '20':'Jharkhand','21':'Odisha','22':'Chhattisgarh','23':'Madhya Pradesh',
    '24':'Gujarat','26':'Daman & Diu','27':'Maharashtra','28':'Andhra Pradesh',
    '29':'Karnataka','30':'Goa','31':'Lakshadweep','32':'Kerala',
    '33':'Tamil Nadu','34':'Puducherry','36':'Telangana','37':'Andhra Pradesh','38':'Ladakh',
};

const INDIAN_STATES = [
    'Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Goa','Gujarat',
    'Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh',
    'Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland','Odisha','Punjab','Rajasthan',
    'Sikkim','Tamil Nadu','Telangana','Tripura','Uttar Pradesh','Uttarakhand','West Bengal',
    'Andaman & Nicobar Islands','Chandigarh','Dadra & Nagar Haveli','Daman & Diu','Delhi',
    'Jammu & Kashmir','Ladakh','Lakshadweep','Puducherry',
];

const NIMAI_STATE = 'Gujarat'; // Nimai Solutions home state
const GST_RATE    = 0.18;      // 18 %

/* ── Date display helper: "2026-03-01" → "01 Mar 2026" ── */
const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '—';
    try {
        const [year, month, day] = dateStr.split('-');
        const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        return `${day} ${months[parseInt(month, 10) - 1]} ${year}`;
    } catch (_) {
        return dateStr;
    }
};

const AdminInvoice = () => {
    /* ── Invoice number series: NS/2025-26/001 ── */
    const generateInvoiceNumber = (seq = 1) => {
        const now = new Date();
        const yr = now.getFullYear();
        const mo = now.getMonth() + 1;          // 1-12
        const fyStart = mo >= 4 ? yr : yr - 1;  // FY starts April
        const fyEnd   = String(fyStart + 1).slice(-2); // e.g. "26"
        return `NS/${fyStart}-${fyEnd}/${String(seq).padStart(3, '0')}`;
    };

    const emptyInvoice = {
        invoice_number: generateInvoiceNumber(),
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        customer_address: '',
        customer_state: '',
        customer_gst: '',
        invoice_date: new Date().toISOString().split('T')[0],
        due_date: '',
        discount: 0,
        paid_amount: 0,
        payment_status: 'unpaid',
        payment_method: 'Cash',
        notes: ''
    };

    const [viewMode, setViewMode] = useState('list'); // 'list' or 'form'
    const [invoicesList, setInvoicesList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [editingId, setEditingId] = useState(null);

    const [invoice, setInvoice] = useState(emptyInvoice);
    const [items, setItems] = useState([
        { id: Date.now(), item_name: '', sac_code: '', description: '', quantity: 1, unit_price: 0, total_price: 0 }
    ]);

    useEffect(() => {
        if (viewMode === 'list') {
            fetchInvoices();
        }
    }, [viewMode]);

    const fetchInvoices = async () => {
        setIsLoading(true);
        try {
            const { ok, data } = await safeFetch('https://aayushrealproperties.in/nimaibackend/public/api/invoices', {
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
                }
            });
            if (ok) {
                // Determine if structure is wrapped under 'invoices', 'data', or is the raw array
                let fetchedInvoices = data?.invoices || data?.data || data;
                if (!Array.isArray(fetchedInvoices)) fetchedInvoices = [];
                setInvoicesList(fetchedInvoices);
            }
        } catch (error) {
            console.error('Error fetching invoices:', error);
            // Non-blocking catch
        } finally {
            setIsLoading(false);
        }
    };

    const handleInvoiceChange = (e) => {
        const { name, value } = e.target;
        // Auto-detect client state from GSTIN (first 2 digits = state code)
        if (name === 'customer_gst' && value.length >= 2) {
            const code = value.slice(0, 2).padStart(2, '0');
            const detectedState = GST_STATE_CODES[code];
            if (detectedState) {
                setInvoice(prev => ({ ...prev, [name]: value, customer_state: detectedState }));
                return;
            }
        }
        setInvoice(prev => ({ ...prev, [name]: value }));
    };

    const handleItemChange = (index, field, value) => {
        const newItems = [...items];
        newItems[index][field] = value;
        if (field === 'quantity' || field === 'unit_price') {
            const qty = parseFloat(newItems[index].quantity) || 0;
            const price = parseFloat(newItems[index].unit_price) || 0;
            newItems[index].total_price = qty * price;
        }
        setItems(newItems);
    };

    const addItem = () => {
        setItems([
            ...items,
            { id: Date.now(), item_name: '', sac_code: '', description: '', quantity: 1, unit_price: 0, total_price: 0 }
        ]);
    };

    const removeItem = (id) => {
        if (items.length > 1) {
            setItems(items.filter(item => item.id !== id));
        } else {
            toast.error("You must have at least one item.");
        }
    };

    const subtotal      = items.reduce((sum, item) => sum + (parseFloat(item.total_price) || 0), 0);
    const discount      = parseFloat(invoice.discount) || 0;
    const taxableAmount = subtotal - discount;
    const isSameState   = invoice.customer_state?.trim().toLowerCase() === NIMAI_STATE.toLowerCase();
    const cgst          = isSameState ? taxableAmount * 0.09 : 0;
    const sgst          = isSameState ? taxableAmount * 0.09 : 0;
    const igst          = !isSameState ? taxableAmount * GST_RATE : 0;
    const gstTotal      = cgst + sgst + igst;
    const totalAmount   = taxableAmount + gstTotal;

    const generatePDF = async (invoiceData, itemsData) => {
        const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
        const PW = 210;           // page width
        const PH = 297;           // page height
        const M = 14;             // margin

        // ── Brand palette ──────────────────────────────────
        const C = {
            dark:   [15,  23,  42],   // #0f172a  (navy)
            orange: [255, 159, 28],   // #FF9F1C  (brand)
            blue:   [59,  130, 246],  // #3b82f6
            light:  [248, 250, 252],  // #f8fafc
            gray:   [100, 116, 139],  // #64748b
            slate:  [71,  85,  105],  // #475569
            muted:  [171, 183, 200],  // light text on dark
            white:  [255, 255, 255],
            green:  [16,  185, 129],
            red:    [239, 68,  68],
        };

        // ── Load logo ──────────────────────────────────────
        let logoImg = null;
        try {
            logoImg = await new Promise((resolve, reject) => {
                const img = new Image();
                img.onload = () => resolve(img);
                img.onerror = reject;
                img.src = logo;
            });
        } catch (e) { /* no logo – fallback text used */ }

        // ── Draw helpers ───────────────────────────────────
        const setFill = (c) => doc.setFillColor(...c);
        const setDraw = (c) => doc.setDrawColor(...c);
        const setTxt  = (c) => doc.setTextColor(...c);
        const bold    = (s = 10) => { doc.setFont("helvetica", "bold");   doc.setFontSize(s); };
        const normal  = (s = 9)  => { doc.setFont("helvetica", "normal"); doc.setFontSize(s); };
        const italic  = (s = 8)  => { doc.setFont("helvetica", "italic"); doc.setFontSize(s); };

        // ── Helpers ───────────────────────────────────────
        // jsPDF Helvetica does NOT support ₹ (U+20B9) — use plain ASCII "INR"
        const inr = (n) => `INR ${parseFloat(n).toFixed(2)}`;
        // Format quantity: drop .000 trailing zeros
        const fmtQty = (q) => {
            const n = parseFloat(q);
            return Number.isInteger(n) ? String(n) : n.toFixed(2);
        };

        // ── PRE-CALCULATIONS ───────────────────────────────
        const invoiceSubtotal  = itemsData.reduce((s, it) => s + (parseFloat(it.total_price) || 0), 0);
        const discountVal      = parseFloat(invoiceData.discount)    || 0;
        const taxable          = invoiceSubtotal - discountVal;
        const clientState      = (invoiceData.customer_state || '').trim().toLowerCase();
        const sameState        = clientState === NIMAI_STATE.toLowerCase();
        const pdfCgst          = sameState ? taxable * 0.09 : 0;
        const pdfSgst          = sameState ? taxable * 0.09 : 0;
        const pdfIgst          = !sameState ? taxable * GST_RATE : 0;
        const pdfGstTotal      = pdfCgst + pdfSgst + pdfIgst;
        const invoiceTotal     = taxable + pdfGstTotal;
        const paidAmt          = parseFloat(invoiceData.paid_amount) || 0;
        const balanceDue       = Math.max(0, invoiceTotal - paidAmt);
        const payStatus        = (invoiceData.payment_status || 'unpaid').toLowerCase();

        // ══════════════════════════════════════════════════
        //  1. HEADER  (dark band + orange top stripe)
        // ══════════════════════════════════════════════════
        setFill(C.dark);  doc.rect(0, 0, PW, 58, 'F');
        setFill(C.orange); doc.rect(0, 0, PW, 3.5, 'F');   // top accent stripe

        // Logo / company name block (left)
        if (logoImg) {
            const lw = 28, lh = (logoImg.height * lw) / logoImg.width;
            doc.addImage(logoImg, 'PNG', M, 11, lw, lh);
            bold(13); setTxt(C.orange);
            doc.text("Nimai Solutions", M + lw + 4, 21);
            normal(7.5); setTxt(C.muted);
            doc.text("Web & Mobile App Development", M + lw + 4, 27);
            doc.text("Vadodara, Gujarat, India", M + lw + 4, 32.5);
            doc.text("hello@nimaisolutions.in", M + lw + 4, 38);
        } else {
            bold(16); setTxt(C.orange);
            doc.text("Nimai Solutions", M, 25);
            normal(7.5); setTxt(C.muted);
            doc.text("Web & Mobile App Development  |  Vadodara, India", M, 32);
        }

        // "TAX INVOICE" title block (right)
        bold(26); setTxt(C.white);
        doc.text("TAX INVOICE", PW - M, 30, { align: "right" });

        // Meta: number, date, due (right-aligned)
        const metaLX = PW - 58, metaVX = PW - M;
        normal(7.8);
        const metaRows = [
            ["Invoice #:",  invoiceData.invoice_number],
            ["Issue Date:", formatDateDisplay(invoiceData.invoice_date)],
            ["Due Date:",   formatDateDisplay(invoiceData.due_date)],
        ];
        metaRows.forEach(([label, val], i) => {
            const y = 37 + i * 6.5;
            setTxt(C.muted);  doc.text(label, metaLX, y);
            setTxt(i === 0 ? C.orange : C.white);
            bold(7.8); doc.text(val, metaVX, y, { align: "right" });
            normal(7.8);
        });

        // Bottom border of header
        setFill(C.orange); doc.rect(0, 57.5, PW, 1, 'F');

        // ══════════════════════════════════════════════════
        //  2. STATUS RIBBON  (method left, badge right)
        // ══════════════════════════════════════════════════
        normal(8); setTxt(C.slate);
        doc.text(`Payment Method: ${invoiceData.payment_method}`, M, 68);

        // Coloured status pill
        const pillColors = {
            paid:    { bg: [220, 252, 231], txt: [22, 163, 74]  },
            partial: { bg: [254, 249, 195], txt: [202, 138, 4]  },
            unpaid:  { bg: [254, 226, 226], txt: [220, 38, 38]  },
        };
        const pill = pillColors[payStatus] || pillColors.unpaid;
        // Pill: x=PW-52, y=62, w=40, h=9  →  center X = PW-52+20 = PW-32
        setFill(pill.bg); doc.roundedRect(PW - 52, 62, 40, 9, 2, 2, 'F');
        bold(7.5); setTxt(pill.txt);
        doc.text(payStatus.toUpperCase(), PW - 32, 67.8, { align: "center" });

        // ══════════════════════════════════════════════════
        //  3. FROM / BILL-TO BOXES
        // ══════════════════════════════════════════════════
        const NIMAI_EMAIL = "hello@nimaisolutions.in";
        const NIMAI_GST   = "24EYHPR9729G1ZU";

        const boxY  = 75, boxH = 50, boxW = 84;

        // FROM box
        setFill([237, 242, 255]); setDraw([196, 210, 255]);
        doc.setLineWidth(0.3);
        doc.roundedRect(M, boxY, boxW, boxH, 3, 3, 'FD');

        bold(7); setTxt(C.orange);
        doc.text("FROM", M + 4, boxY + 7);
        bold(10); setTxt(C.dark);
        doc.text("Nimai Solutions", M + 4, boxY + 14);
        normal(8); setTxt(C.gray);
        doc.text("Web & Mobile App Development", M + 4, boxY + 20.5);
        doc.text("Vadodara, Gujarat, India",     M + 4, boxY + 26.5);
        doc.text(NIMAI_EMAIL,                    M + 4, boxY + 32.5);
        // GST line with a slightly different style
        normal(7.5); setTxt(C.slate);
        doc.text("GSTIN: ", M + 4, boxY + 39.5);
        bold(7.5); setTxt(C.dark);
        doc.text(NIMAI_GST, M + 18, boxY + 39.5);

        // BILL TO box
        const toX = PW - M - boxW;
        setFill(C.light); setDraw([226, 232, 240]);
        doc.roundedRect(toX, boxY, boxW, boxH, 3, 3, 'FD');

        bold(7); setTxt(C.blue);
        doc.text("BILL TO", toX + 4, boxY + 7);
        bold(10); setTxt(C.dark);
        doc.text(invoiceData.customer_name || "—", toX + 4, boxY + 14);
        normal(8); setTxt(C.gray);
        let toLine = boxY + 20.5;
        if (invoiceData.customer_email)   { doc.text(invoiceData.customer_email,   toX + 4, toLine); toLine += 6; }
        if (invoiceData.customer_phone)   { doc.text(invoiceData.customer_phone,   toX + 4, toLine); toLine += 6; }
        if (invoiceData.customer_address) {
            const parts = doc.splitTextToSize(invoiceData.customer_address, boxW - 8);
            doc.text(parts[0], toX + 4, toLine);
            toLine += 6;
        }
        if (invoiceData.customer_gst) {
            normal(7.5); setTxt(C.slate);
            doc.text("GSTIN: ", toX + 4, toLine);
            bold(7.5); setTxt(C.dark);
            doc.text(invoiceData.customer_gst.toUpperCase(), toX + 18, toLine);
        }

        // ══════════════════════════════════════════════════
        //  4. ITEMS TABLE
        // ══════════════════════════════════════════════════
        const tableY = boxY + boxH + 8;
        // Available width = 210 - 14 - 14 = 182mm
        // Columns: 8 + 40 + 18 + 32 + 14 + 34 + 36 = 182
        autoTable(doc, {
            startY: tableY,
            head: [["#", "Service / Description", "SAC Code", "Details", "Qty", "Unit Price", "Amount"]],
            body: itemsData.map((it, i) => [
                i + 1,
                it.item_name   || "—",
                it.sac_code    || "—",
                it.description || "—",
                fmtQty(it.quantity),
                inr(it.unit_price),
                inr(it.total_price),
            ]),
            theme: 'plain',
            margin: { left: M, right: M },
            styles: {
                fontSize: 8,
                font: "helvetica",
                cellPadding: { top: 5, right: 4, bottom: 5, left: 4 },
                textColor: C.dark,
                lineColor: [226, 232, 240],
                lineWidth: 0.25,
                overflow: 'linebreak',
                minCellHeight: 10,
            },
            headStyles: {
                fillColor: C.dark,
                textColor: C.white,
                fontStyle: "bold",
                fontSize: 7.5,
                cellPadding: { top: 6, right: 4, bottom: 6, left: 4 },
                minCellHeight: 12,
            },
            alternateRowStyles: { fillColor: [248, 250, 252] },
            columnStyles: {
                0: { cellWidth: 8,  halign: 'center', textColor: C.gray  },
                1: { cellWidth: 40, fontStyle: 'bold'                     },
                2: { cellWidth: 18, halign: 'center', textColor: C.gray  },
                3: { cellWidth: 32, textColor: C.gray                     },
                4: { cellWidth: 14, halign: 'center'                      },
                5: { cellWidth: 34, halign: 'right'                       },
                6: { cellWidth: 36, halign: 'right', fontStyle: 'bold'    },
            },
        });

        const afterTable = doc.lastAutoTable.finalY + 8;

        // ══════════════════════════════════════════════════
        //  5. NOTES (left) + SUMMARY BOX (right)
        // ══════════════════════════════════════════════════
        const sumW = 90, sumX = PW - M - sumW;

        // Build GST rows dynamically
        const gstRows = sameState
            ? [
                { label: "CGST (9%)",  val: inr(pdfCgst), color: C.slate, bg: null, isBold: false, isDivider: false },
                { label: "SGST (9%)",  val: inr(pdfSgst), color: C.slate, bg: null, isBold: false, isDivider: false },
              ]
            : [
                { label: "IGST (18%)", val: inr(pdfIgst), color: C.slate, bg: null, isBold: false, isDivider: false },
              ];

        const sumRows = [
            { label: "Subtotal",           val: inr(invoiceSubtotal), color: C.dark,  bg: null,   isBold: false, isDivider: false },
            { label: `Discount`,           val: `- ${inr(discountVal)}`, color: C.red, bg: null,  isBold: false, isDivider: false },
            { label: "Taxable Amount",     val: inr(taxable),         color: C.dark,  bg: null,   isBold: false, isDivider: true  },
            ...gstRows,
            { label: "Total  (incl. GST)", val: inr(invoiceTotal),    color: C.white, bg: C.dark, isBold: true,  isDivider: true  },
            { label: "Amount Paid",        val: inr(paidAmt),         color: C.green, bg: null,   isBold: false, isDivider: false },
            { label: "Balance Due",        val: inr(balanceDue),      color: balanceDue > 0 ? C.red : C.green, bg: null, isBold: true, isDivider: false },
        ];

        // Calculate summary box height dynamically
        const rowH = 9.5, sumPad = 6;
        const sumBoxH = sumPad + sumRows.length * rowH + sumPad;
        setFill(C.light); setDraw([226, 232, 240]); doc.setLineWidth(0.3);
        doc.roundedRect(sumX, afterTable, sumW, sumBoxH, 3, 3, 'FD');

        let rowY = afterTable + sumPad + 5;
        sumRows.forEach((row) => {
            // Divider line before this row
            if (row.isDivider) {
                setDraw([209, 213, 219]); doc.setLineWidth(0.35);
                doc.line(sumX + 5, rowY - 6, sumX + sumW - 5, rowY - 6);
            }
            // Highlighted background row
            if (row.bg) {
                setFill(row.bg);
                doc.roundedRect(sumX + 2, rowY - 5.5, sumW - 4, 9.5, 2, 2, 'F');
            }
            if (row.isBold) { bold(9); } else { normal(8.5); }
            setTxt(row.bg ? C.white : C.gray);
            doc.text(row.label, sumX + 6, rowY);
            setTxt(row.color);
            doc.text(row.val, sumX + sumW - 6, rowY, { align: "right" });
            rowY += rowH;
        });

        // Notes block (left-side, same vertical band as summary)
        if (invoiceData.notes) {
            bold(8.5); setTxt(C.dark);
            doc.text("Notes", M, afterTable + 5);
            // underline
            setDraw(C.orange); doc.setLineWidth(0.8);
            doc.line(M, afterTable + 6.5, M + 10, afterTable + 6.5);
            normal(8); setTxt(C.gray);
            const noteLines = doc.splitTextToSize(invoiceData.notes, sumX - M - 6);
            doc.text(noteLines, M, afterTable + 13);
        }

        // Payment status text (below notes)
        const belowSum = afterTable + sumBoxH + 6;
        normal(8); setTxt(C.slate);
        doc.text(`Payment Status: `, M, belowSum);
        bold(8); setTxt(pill.txt);
        doc.text(payStatus.toUpperCase(), M + 28, belowSum);

        // ══════════════════════════════════════════════════
        //  6. PAID WATERMARK (subtle, diagonal)
        // ══════════════════════════════════════════════════
        if (payStatus === 'paid') {
            try {
                doc.saveGraphicsState();
                doc.setGState(new doc.GState({ opacity: 0.07 }));
                doc.setTextColor(22, 163, 74);
                doc.setFontSize(72);
                doc.setFont("helvetica", "bold");
                doc.text("PAID", PW / 2, PH / 2 + 20, { align: "center", angle: 35 });
                doc.restoreGraphicsState();
            } catch (_) { /* skip watermark if GState unsupported */ }
        }

        // ══════════════════════════════════════════════════
        //  7. FOOTER BAND
        // ══════════════════════════════════════════════════
        const ftY = PH - 24;
        setFill(C.dark); doc.rect(0, ftY, PW, 24, 'F');
        setFill(C.orange); doc.rect(0, ftY, PW, 1.5, 'F');  // top stripe

        bold(9); setTxt(C.orange);
        doc.text("Thank you for choosing Nimai Solutions!", PW / 2, ftY + 9, { align: "center" });

        italic(7.5); setTxt(C.muted);
        doc.text("Computer Generated Tax Invoice — No Physical Signature Required", PW / 2, ftY + 15.5, { align: "center" });

        normal(7); setTxt([80, 95, 115]);
        doc.text("Nimai Solutions  |  nimaisolutions.in  |  hello@nimaisolutions.in", PW / 2, ftY + 21, { align: "center" });

        doc.save(`${invoiceData.invoice_number}.pdf`);
        toast.success(`Invoice ${invoiceData.invoice_number} saved as PDF!`);
    };

    const handleSaveAndPDF = async (e) => {
        e.preventDefault();

        if (!invoice.customer_name) {
            toast.error("Customer name is required.");
            return;
        }

        const invoicePayload = {
            ...invoice,
            subtotal:        subtotal.toFixed(2),
            taxable_amount:  taxableAmount.toFixed(2),
            cgst_amount:     cgst.toFixed(2),
            sgst_amount:     sgst.toFixed(2),
            igst_amount:     igst.toFixed(2),
            gst_total:       gstTotal.toFixed(2),
            total_amount:    totalAmount.toFixed(2),
            is_same_state:   isSameState,
            items: items.map(item => ({
                item_name:   item.item_name,
                sac_code:    item.sac_code    || '',
                description: item.description || '',
                quantity:    item.quantity,
                unit_price:  item.unit_price,
                total_price: (parseFloat(item.total_price) || 0).toFixed(2)
            }))
        };

        const url = editingId
            ? `https://aayushrealproperties.in/nimaibackend/public/api/invoices/${editingId}`
            : 'https://aayushrealproperties.in/nimaibackend/public/api/invoices';
        const method = editingId ? 'PUT' : 'POST';

        // Trigger local PDF first so it doesnt block waiting on network if needed
        await generatePDF(invoicePayload, invoicePayload.items);

        try {
            const { ok, data } = await safeFetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
                },
                body: JSON.stringify(invoicePayload)
            });

            if (ok) {
                toast.success(`Tax Invoice ${editingId ? 'updated' : 'created'} securely in backend.`);
                // Return to list view
                setInvoice(emptyInvoice);
                setItems([{ id: Date.now(), item_name: '', sac_code: '', description: '', quantity: 1, unit_price: 0, total_price: 0 }]);
                setEditingId(null);
                setViewMode('list');
            } else {
                toast.error(data?.message || 'Failed to save invoice fully online, PDF saved.');
            }
        } catch (error) {
            console.error('Save error:', error);
            toast.error('Network Error. Document only saved locally.');
        }
    };

    const handleEdit = async (inv) => {
        setIsLoading(true);
        // Sometimes backend returns items, if not we rely on fetching specific id
        try {
            const { ok, data } = await safeFetch(`https://aayushrealproperties.in/nimaibackend/public/api/invoices/${inv.id}`, {
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
                }
            });
            if (ok) {
                const fetchedInv = data.invoice || data.data || data;
                setInvoice({
                    invoice_number:   fetchedInv.invoice_number   || generateInvoiceNumber(),
                    customer_name:    fetchedInv.customer_name    || '',
                    customer_email:   fetchedInv.customer_email   || '',
                    customer_phone:   fetchedInv.customer_phone   || '',
                    customer_address: fetchedInv.customer_address || '',
                    customer_state:   fetchedInv.customer_state   || '',
                    customer_gst:     fetchedInv.customer_gst     || '',
                    invoice_date:     fetchedInv.invoice_date     || new Date().toISOString().split('T')[0],
                    due_date:         fetchedInv.due_date         || '',
                    discount:         fetchedInv.discount         || 0,
                    paid_amount:      fetchedInv.paid_amount      || 0,
                    payment_status:   fetchedInv.payment_status   || 'unpaid',
                    payment_method:   fetchedInv.payment_method   || 'Cash',
                    notes:            fetchedInv.notes            || ''
                });

                if (fetchedInv.items && fetchedInv.items.length > 0) {
                    setItems(fetchedInv.items.map(item => ({
                        id:          item.id || Date.now() + Math.random(),
                        item_name:   item.item_name   || '',
                        sac_code:    item.sac_code    || '',
                        description: item.description || '',
                        quantity:    item.quantity    || 1,
                        unit_price:  item.unit_price  || 0,
                        total_price: item.total_price || 0
                    })));
                } else {
                    setItems([{ id: Date.now(), item_name: '', sac_code: '', description: '', quantity: 1, unit_price: 0, total_price: 0 }]);
                }

                setEditingId(inv.id);
                setViewMode('form');
            } else {
                toast.error('Failed to grab full invoice details.');
            }
        } catch (err) {
            toast.error('Network Error getting details.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this invoice?")) return;

        try {
            const { ok } = await safeFetch(`https://aayushrealproperties.in/nimaibackend/public/api/invoices/${id}`, {
                method: 'DELETE',
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
                }
            });
            if (ok) {
                toast.success('Invoice deleted successfully');
                fetchInvoices();
            } else {
                toast.error('Failed to delete.');
            }
        } catch (err) {
            toast.error('Deletion network error.');
        }
    };

    const handleAddNew = () => {
        // Derive next series number from existing invoices in same financial year
        const now = new Date();
        const yr = now.getFullYear();
        const mo = now.getMonth() + 1;
        const fyStart = mo >= 4 ? yr : yr - 1;
        const fyEnd   = String(fyStart + 1).slice(-2);
        const prefix  = `NS/${fyStart}-${fyEnd}/`;
        let maxSeq = 0;
        invoicesList.forEach(inv => {
            if (inv.invoice_number && inv.invoice_number.startsWith(prefix)) {
                const seq = parseInt(inv.invoice_number.replace(prefix, ''), 10);
                if (!isNaN(seq) && seq > maxSeq) maxSeq = seq;
            }
        });
        setInvoice({ ...emptyInvoice, invoice_number: generateInvoiceNumber(maxSeq + 1) });
        setItems([{ id: Date.now(), item_name: '', sac_code: '', description: '', quantity: 1, unit_price: 0, total_price: 0 }]);
        setEditingId(null);
        setViewMode('form');
    };

    return (
        <div className="admin-layout">
            <Sidebar />
            <main className="admin-content">
                <header className="content-header">
                    <div>
                        <h1>{viewMode === 'list' ? 'Tax Invoice' : 'Generate'} <span>{viewMode === 'list' ? 'Manager' : 'Tax Invoice'}</span></h1>
                        <p>{viewMode === 'list' ? 'Manage your saved tax invoices securely.' : 'Create and manage GST-compliant tax invoices.'}</p>
                    </div>
                    {viewMode === 'list' ? (
                        <div className="admin-actions">
                            <button className="btn-add-stat" onClick={handleAddNew}>
                                <FaPlus /> Create Tax Invoice
                            </button>
                        </div>
                    ) : (
                        <div className="admin-actions">
                            <button className="btn-cancel" onClick={() => setViewMode('list')} style={{ background: '#f1f5f9', color: '#0f172a', border: 'none', padding: '10px 15px', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 'bold' }}>
                                <FaArrowLeft /> Back to List
                            </button>
                        </div>
                    )}
                </header>

                <AnimatePresence mode="wait">
                    {viewMode === 'list' && (
                        <motion.div
                            key="list-view"
                            className="invoice-container"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 20 }}
                        >
                            <div className="items-table-container">
                                {isLoading ? (
                                    <div style={{ padding: '30px', textAlign: 'center' }}>Loading invoices...</div>
                                ) : (
                                    <table className="items-table">
                                        <thead>
                                            <tr>
                                                <th>INV Number</th>
                                                <th>Client Info</th>
                                                <th>Date</th>
                                                <th>Total</th>
                                                <th>Status</th>
                                                <th style={{ textAlign: 'center' }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {invoicesList.length === 0 ? (
                                                <tr>
                                                    <td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>No invoices created yet.</td>
                                                </tr>
                                            ) : (
                                                invoicesList.map((inv) => (
                                                    <tr key={inv.id}>
                                                        <td style={{ fontWeight: 'bold', color: '#3b82f6' }}>{inv.invoice_number}</td>
                                                        <td>
                                                            <div>{inv.customer_name}</div>
                                                            <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{inv.customer_email || 'No email'}</div>
                                                        </td>
                                                        <td>{formatDateDisplay(inv.invoice_date)}</td>
                                                        <td style={{ fontWeight: 'bold' }}>₹{inv.total_amount}</td>
                                                        <td>
                                                            <span style={{
                                                                padding: '5px 10px',
                                                                borderRadius: '20px',
                                                                fontSize: '0.8rem',
                                                                fontWeight: 'bold',
                                                                background: inv.payment_status === 'paid' ? '#dcfce7' : inv.payment_status === 'partial' ? '#fef9c3' : '#fee2e2',
                                                                color: inv.payment_status === 'paid' ? '#16a34a' : inv.payment_status === 'partial' ? '#ca8a04' : '#dc2626'
                                                            }}>
                                                                {inv.payment_status?.toUpperCase()}
                                                            </span>
                                                        </td>
                                                        <td style={{ textAlign: 'center' }}>
                                                            <button
                                                                onClick={() => handleEdit(inv)}
                                                                style={{ background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', margin: '0 8px', fontSize: '1.2rem' }}
                                                                title="Edit & Resave PDF"
                                                            >
                                                                <FaEdit />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDelete(inv.id)}
                                                                style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', margin: '0 8px', fontSize: '1.2rem' }}
                                                                title="Delete"
                                                            >
                                                                <FaTrash />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </motion.div>
                    )}

                    {viewMode === 'form' && (
                        <motion.div
                            key="form-view"
                            className="invoice-container"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                        >
                            <div className="invoice-header-actions">
                                <h2><FaFileInvoiceDollar /> {editingId ? 'Edit Tax Invoice Details' : 'New Tax Invoice Details'}</h2>
                            </div>

                            <form onSubmit={handleSaveAndPDF}>
                                <div className="form-section">
                                    <h3 className="form-section-title">Invoice Information</h3>
                                    <div className="form-grid-3">
                                        <div className="form-group">
                                            <label>Invoice Number</label>
                                            <input type="text" name="invoice_number" value={invoice.invoice_number} readOnly className="read-only" style={{ backgroundColor: '#e2e8f0', color: '#475569' }} />
                                        </div>
                                        <div className="form-group">
                                            <label>Invoice Date</label>
                                            <input type="date" name="invoice_date" value={invoice.invoice_date} onChange={handleInvoiceChange} required />
                                        </div>
                                        <div className="form-group">
                                            <label>Due Date</label>
                                            <input type="date" name="due_date" value={invoice.due_date} onChange={handleInvoiceChange} required />
                                        </div>
                                    </div>
                                </div>

                                <div className="form-section">
                                    <h3 className="form-section-title">Client Information</h3>
                                    <div className="form-grid-3">
                                        <div className="form-group">
                                            <label>Customer Name</label>
                                            <input type="text" name="customer_name" value={invoice.customer_name} onChange={handleInvoiceChange} placeholder="Enter Client Name" required />
                                        </div>
                                        <div className="form-group">
                                            <label>Email Address</label>
                                            <input type="email" name="customer_email" value={invoice.customer_email} onChange={handleInvoiceChange} placeholder="Client Email (Optional)" />
                                        </div>
                                        <div className="form-group">
                                            <label>Phone Number</label>
                                            <input type="tel" name="customer_phone" value={invoice.customer_phone} onChange={handleInvoiceChange} placeholder="Client Phone (Optional)" />
                                        </div>
                                    </div>
                                    <div className="form-grid-2">
                                        <div className="form-group">
                                            <label>Billing Address</label>
                                            <textarea name="customer_address" value={invoice.customer_address} onChange={handleInvoiceChange} placeholder="Enter Billing Address" required></textarea>
                                        </div>
                                        <div className="form-group">
                                            <label>
                                                Client State
                                                <span style={{ fontSize: '0.78rem', color: '#94a3b8', fontWeight: 400, marginLeft: '6px' }}>(Required for GST)</span>
                                            </label>
                                            <select
                                                name="customer_state"
                                                value={invoice.customer_state}
                                                onChange={handleInvoiceChange}
                                                required
                                                style={{ textTransform: 'capitalize' }}
                                            >
                                                <option value="">-- Select State --</option>
                                                {INDIAN_STATES.map(s => (
                                                    <option key={s} value={s}>{s}</option>
                                                ))}
                                            </select>
                                            {invoice.customer_state && (
                                                <span style={{
                                                    fontSize: '0.78rem',
                                                    fontWeight: 600,
                                                    marginTop: '4px',
                                                    color: invoice.customer_state.toLowerCase() === NIMAI_STATE.toLowerCase() ? '#16a34a' : '#2563eb'
                                                }}>
                                                    {invoice.customer_state.toLowerCase() === NIMAI_STATE.toLowerCase()
                                                        ? '✓ Same State — CGST 9% + SGST 9% will apply'
                                                        : '✓ Different State — IGST 18% will apply'}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="form-grid-2">
                                        <div className="form-group">
                                            <label>
                                                Client GST Number
                                                <span style={{ fontSize: '0.78rem', color: '#94a3b8', fontWeight: 400, marginLeft: '6px' }}>(Optional — auto-detects state)</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="customer_gst"
                                                value={invoice.customer_gst}
                                                onChange={handleInvoiceChange}
                                                placeholder="e.g. 24XXXXX0000X1ZX"
                                                maxLength={15}
                                                style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}
                                            />
                                            <span style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '2px' }}>
                                                State is auto-detected from first 2 digits of GSTIN
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="form-section">
                                    <h3 className="form-section-title">Invoice Items</h3>
                                    <div className="items-table-container">
                                        <table className="items-table">
                                            <thead>
                                                <tr>
                                                    <th>Service / Description</th>
                                                    <th style={{ width: '110px' }}>SAC Code</th>
                                                    <th>Details</th>
                                                    <th className="qty-col">Qty</th>
                                                    <th className="price-col">Price</th>
                                                    <th className="total-col">Total</th>
                                                    <th style={{ width: '50px' }}></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {items.map((item, index) => (
                                                    <tr key={item.id}>
                                                        <td>
                                                            <input
                                                                type="text"
                                                                placeholder="Service Name"
                                                                value={item.item_name}
                                                                onChange={(e) => handleItemChange(index, 'item_name', e.target.value)}
                                                                required
                                                            />
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="text"
                                                                placeholder="e.g. 998314"
                                                                value={item.sac_code}
                                                                onChange={(e) => handleItemChange(index, 'sac_code', e.target.value)}
                                                                style={{ textAlign: 'center', letterSpacing: '0.05em' }}
                                                            />
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="text"
                                                                placeholder="Description (Optional)"
                                                                value={item.description}
                                                                onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                                                            />
                                                        </td>
                                                        <td className="qty-col">
                                                            <input
                                                                type="number"
                                                                min="1"
                                                                step="0.01"
                                                                value={item.quantity}
                                                                onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                                                required
                                                            />
                                                        </td>
                                                        <td className="price-col">
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                step="0.01"
                                                                value={item.unit_price}
                                                                onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)}
                                                                required
                                                            />
                                                        </td>
                                                        <td className="total-col">
                                                            ₹{parseFloat(item.total_price).toFixed(2)}
                                                        </td>
                                                        <td>
                                                            <button type="button" className="btn-remove-item" onClick={() => removeItem(item.id)}>
                                                                <FaTrash />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <button type="button" className="btn-add-item" onClick={addItem}>
                                        <FaPlus /> Add Line Item
                                    </button>
                                </div>

                                <div className="form-section">
                                    <div className="form-grid-2">
                                        <div>
                                            <h3 className="form-section-title">Payment Details</h3>
                                            <div className="form-grid-2">
                                                <div className="form-group">
                                                    <label>Payment Status</label>
                                                    <select name="payment_status" value={invoice.payment_status} onChange={handleInvoiceChange}>
                                                        <option value="unpaid">Unpaid</option>
                                                        <option value="partial">Partial</option>
                                                        <option value="paid">Paid</option>
                                                    </select>
                                                </div>
                                                <div className="form-group">
                                                    <label>Payment Method</label>
                                                    <select name="payment_method" value={invoice.payment_method} onChange={handleInvoiceChange}>
                                                        <option value="System">Select Method</option>
                                                        <option value="Cash">Cash</option>
                                                        <option value="UPI">UPI / NetBanking</option>
                                                        <option value="Card">Credit / Debit Card</option>
                                                        <option value="Bank Transfer">Bank Transfer</option>
                                                    </select>
                                                </div>
                                            </div>

                                            <div className="form-group">
                                                <label>Paid Amount</label>
                                                <input type="number" name="paid_amount" min="0" step="0.01" value={invoice.paid_amount} onChange={handleInvoiceChange} placeholder="Enter Received Amount" />
                                            </div>

                                            <div className="form-group">
                                                <label>Additional Notes</label>
                                                <textarea name="notes" value={invoice.notes} onChange={handleInvoiceChange} placeholder="Message or terms & conditions..." rows="3"></textarea>
                                            </div>
                                        </div>

                                        <div className="invoice-summary">
                                            <div className="summary-box">
                                                <div className="summary-row">
                                                    <span>Subtotal</span>
                                                    <span>₹{subtotal.toFixed(2)}</span>
                                                </div>
                                                <div className="summary-row">
                                                    <span>Discount</span>
                                                    <input
                                                        type="number"
                                                        name="discount"
                                                        min="0"
                                                        step="0.01"
                                                        value={invoice.discount}
                                                        onChange={handleInvoiceChange}
                                                    />
                                                </div>
                                                <div className="summary-row" style={{ borderTop: '1px dashed #e2e8f0', paddingTop: '8px', marginTop: '4px' }}>
                                                    <span>Taxable Amount</span>
                                                    <span>₹{taxableAmount.toFixed(2)}</span>
                                                </div>

                                                {/* GST Breakdown */}
                                                {invoice.customer_state ? (
                                                    isSameState ? (
                                                        <>
                                                            <div className="summary-row gst-row">
                                                                <span style={{ color: '#2563eb' }}>CGST (9%)</span>
                                                                <span style={{ color: '#2563eb' }}>+ ₹{cgst.toFixed(2)}</span>
                                                            </div>
                                                            <div className="summary-row gst-row">
                                                                <span style={{ color: '#2563eb' }}>SGST (9%)</span>
                                                                <span style={{ color: '#2563eb' }}>+ ₹{sgst.toFixed(2)}</span>
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <div className="summary-row gst-row">
                                                            <span style={{ color: '#7c3aed' }}>IGST (18%)</span>
                                                            <span style={{ color: '#7c3aed' }}>+ ₹{igst.toFixed(2)}</span>
                                                        </div>
                                                    )
                                                ) : (
                                                    <div className="summary-row gst-row" style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '0.8rem' }}>
                                                        <span>GST (18%)</span>
                                                        <span>Select state above</span>
                                                    </div>
                                                )}

                                                <div className="summary-row total" style={{ borderTop: '2px solid #0f172a', marginTop: '6px' }}>
                                                    <span>Total (incl. GST)</span>
                                                    <span>₹{totalAmount.toFixed(2)}</span>
                                                </div>
                                                <div className="summary-row" style={{ marginTop: '10px', fontSize: '0.9rem' }}>
                                                    <span>Amount Paid</span>
                                                    <span style={{ color: '#10b981' }}>₹{parseFloat(invoice.paid_amount || 0).toFixed(2)}</span>
                                                </div>
                                                <div className="summary-row" style={{ fontWeight: '500' }}>
                                                    <span>Balance Due</span>
                                                    <span style={{ color: '#ef4444' }}>₹{Math.max(0, totalAmount - (parseFloat(invoice.paid_amount) || 0)).toFixed(2)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="invoice-actions">
                                    <button type="button" className="btn-cancel" onClick={() => setViewMode('list')}>
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn-generate">
                                        <FaSave /> {editingId ? 'Update & Save PDF' : 'Save Tax Invoice & PDF'}
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    )}
                </AnimatePresence>
            </main>
        </div>
    );
};

export default AdminInvoice;
